import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Flex,
  Text,
  Center,
  Icon,
  Skeleton,
  CircularProgress,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import {
  FaMapMarkerAlt,
  FaChevronRight,
  FaLocationArrow,
  FaHome,
} from "react-icons/fa";
import googleMapsService from "../../Services/GoogleMapsService";
import {
  guidesWithAddresses,
  geocodeGuideAddresses,
} from "../Logic/guideLocations";
import { getFormattedAddress } from "../Logic/propertyGuideItems";
import { getGuideCategory, PROPERTY_CATEGORY } from "../Logic/guideCategories";
import { buildPinElement } from "./mapPins";

const PROPERTY_ID = "__property";
// A city guide set can hold dozens of places; the strip is for "what's right
// here", so it's capped to the closest handful.
const MAX_IN_VIEW = 15;

const removeMarker = (marker) => {
  if (!marker) return;
  // Legacy markers use setMap; AdvancedMarkerElement exposes `map` instead.
  if (typeof marker.setMap === "function") marker.setMap(null);
  else marker.map = null;
};

const buildUserDot = () => {
  const dot = document.createElement("div");
  dot.style.cssText =
    "width:18px;height:18px;border-radius:50%;background:#4299E1;border:3px solid #fff;" +
    "box-shadow:0 0 0 6px rgba(66,153,225,0.3);";
  return dot;
};

// One map for everywhere a guest might go: the property plus every guide that
// names an address. It fills the drawer, and the strip along the bottom lists
// only the places inside the current view — pan or zoom and it follows — so
// the map is the browser and the list never becomes a wall of rows.
export const GuidesMap = ({ room, guides, loading, onGuideSelect }) => {
  const toast = useToast();

  const [positions, setPositions] = useState({});
  const [propertyPosition, setPropertyPosition] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [resolvedOnce, setResolvedOnce] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [inViewIds, setInViewIds] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [findingUser, setFindingUser] = useState(false);

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerStoreRef = useRef({});
  const userMarkerRef = useRef(null);
  const interactedRef = useRef(false);
  const cardRefs = useRef({});
  const pinsRef = useRef([]);
  const propertyPositionRef = useRef(null);
  const selectedIdRef = useRef(null);
  const boundsTimerRef = useRef(null);
  const stripRef = useRef(null);
  const stripTouchedRef = useRef(false);
  // True only while the strip is moving because the guest moved it. The strip
  // also scrolls programmatically (reset after map pans, scrollIntoView on pin
  // taps), and those scrolls must not be read back as navigation.
  const stripUserScrollRef = useRef(false);
  const stripScrollTimerRef = useRef(null);
  // Who moved the viewport last: "map" (drag, zoom, jump buttons) or "strip"
  // (swiping the cards). After a map move the strip rewinds to the nearest
  // places; after a strip move it must hold the card the guest swiped to.
  const lastNavSourceRef = useRef("map");

  const located = useMemo(() => guidesWithAddresses(guides), [guides]);

  // Addresses are the real input to geocoding, so key the effect off them
  // rather than the guide array's identity, which changes on every fetch.
  const addressSignature = useMemo(
    () => located.map((entry) => `${entry.id}:${entry.address}`).join("|"),
    [located]
  );

  useEffect(() => {
    if (located.length === 0) {
      setPositions({});
      return undefined;
    }

    let cancelled = false;
    setIsLocating(true);

    geocodeGuideAddresses(located, { isCancelled: () => cancelled })
      .then((resolved) => {
        if (!cancelled) setPositions(resolved);
      })
      .catch((error) => {
        console.error("Error locating guides:", error);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLocating(false);
          setResolvedOnce(true);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressSignature]);

  const propertyAddress = getFormattedAddress(room);

  useEffect(() => {
    if (!propertyAddress) {
      setPropertyPosition(null);
      return undefined;
    }

    let cancelled = false;

    geocodeGuideAddresses([{ id: "property", address: propertyAddress }], {
      isCancelled: () => cancelled,
    })
      .then((resolved) => {
        if (!cancelled) setPropertyPosition(resolved.property || null);
      })
      .catch(() => {
        // The map still works without the home pin.
      });

    return () => {
      cancelled = true;
    };
  }, [propertyAddress]);

  const pins = useMemo(
    () =>
      located
        .map((entry) => ({ ...entry, position: positions[entry.id] }))
        .filter((entry) => entry.position),
    [located, positions]
  );

  pinsRef.current = pins;
  propertyPositionRef.current = propertyPosition;
  selectedIdRef.current = selectedId;

  const center = propertyPosition || pins[0]?.position || null;
  const canShowMap = !loading && Boolean(center);

  // Works out which pins sit inside the visible window, nearest the middle
  // first. Runs on every idle (after any pan or zoom) and after pins change.
  const updateInView = useCallback(() => {
    const map = mapRef.current;
    const bounds = map?.getBounds?.();
    if (!map || !bounds) return;

    const mapCenter = map.getCenter();
    const cLat = mapCenter.lat();
    const cLng = mapCenter.lng();

    const visible = pinsRef.current
      .filter((pin) => bounds.contains(pin.position))
      .map((pin) => ({
        id: pin.id,
        // Flat-earth distance is plenty to order pins within one viewport.
        distance:
          (pin.position.lat - cLat) ** 2 + (pin.position.lng - cLng) ** 2,
      }))
      .sort((a, b) => a.distance - b.distance);

    let ids = visible.slice(0, MAX_IN_VIEW).map((item) => item.id);

    // The property is a place like any other once it's in view, but it's
    // the anchor of the map, so it leads the strip instead of being ranked by
    // distance — and it doesn't use up one of the guide slots.
    const home = propertyPositionRef.current;
    if (home && bounds.contains(home)) {
      ids = [PROPERTY_ID, ...ids];
    }

    // A tapped pin must always have its card, even if it isn't among the
    // closest to the centre. Once it leaves the view altogether, drop the
    // selection so it doesn't stay highlighted for a place that's gone.
    const selected = selectedIdRef.current;
    if (selected && selected !== PROPERTY_ID) {
      if (visible.some((item) => item.id === selected)) {
        if (!ids.includes(selected)) {
          ids = [selected, ...ids.slice(0, MAX_IN_VIEW - 1)];
        }
      } else {
        setSelectedId(null);
      }
    }

    setInViewIds((prev) =>
      prev.length === ids.length && prev.every((id, i) => id === ids[i])
        ? prev
        : ids
    );
  }, []);

  const applySelection = useCallback(() => {
    Object.entries(markerStoreRef.current).forEach(([id, entry]) => {
      const isSelected = id === selectedIdRef.current;
      entry.element.style.transform = isSelected ? "scale(1.3)" : "scale(1)";
      try {
        entry.marker.zIndex = id === PROPERTY_ID ? 900 : isSelected ? 1000 : 0;
      } catch (error) {
        // Legacy markers ignore this; selection styling is cosmetic.
      }
    });
  }, []);

  // Create the map once there's somewhere to centre it. Keyed on
  // `canShowMap` because that's when the container div exists.
  useEffect(() => {
    if (!canShowMap || !containerRef.current) return undefined;

    let cancelled = false;
    setMapError(false);

    (async () => {
      try {
        const map = await googleMapsService.initializeMap(
          containerRef.current,
          {
            center: { lat: center.lat, lng: center.lng },
            zoom: 14,
            zoomControl: true,
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            clickableIcons: false,
            // The map fills the drawer and nothing scrolls around it, so
            // one-finger drag should move it.
            gestureHandling: "greedy",
          }
        );
        if (cancelled) return;

        // Keep the zoom buttons clear of the card strip along the bottom.
        map.setOptions({
          zoomControlOptions: {
            position: window.google.maps.ControlPosition.RIGHT_CENTER,
          },
        });
        map.addListener("dragstart", () => {
          interactedRef.current = true;
          lastNavSourceRef.current = "map";
        });
        // Fires for the zoom buttons and pinches; strip-driven pans never
        // change zoom, so this cleanly flips navigation back to the map.
        map.addListener("zoom_changed", () => {
          lastNavSourceRef.current = "map";
        });
        // `idle` only fires once a pan or zoom has fully settled, which made
        // the strip feel a beat behind. `bounds_changed` fires all through
        // the gesture, so refresh on it too — throttled, since it can fire
        // dozens of times a second and each refresh filters every pin.
        map.addListener("idle", updateInView);
        map.addListener("bounds_changed", () => {
          if (boundsTimerRef.current) return;
          boundsTimerRef.current = setTimeout(() => {
            boundsTimerRef.current = null;
            updateInView();
          }, 100);
        });

        mapRef.current = map;
        setMapReady(true);
      } catch (error) {
        console.error("Error initializing guides map:", error);
        if (!cancelled) setMapError(true);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(boundsTimerRef.current);
      boundsTimerRef.current = null;
      Object.values(markerStoreRef.current).forEach(({ marker }) =>
        removeMarker(marker)
      );
      markerStoreRef.current = {};
      removeMarker(userMarkerRef.current);
      userMarkerRef.current = null;
      mapRef.current = null;
      setMapReady(false);
    };
    // The centre only matters at creation; later pin changes are handled by
    // the marker effect without rebuilding the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShowMap]);

  // Keep the pins in step with the data. The map itself is never rebuilt.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return undefined;

    let cancelled = false;

    const specs = [];
    if (propertyPosition) {
      specs.push({
        id: PROPERTY_ID,
        position: propertyPosition,
        category: PROPERTY_CATEGORY,
        title: "Your stay",
        isProperty: true,
      });
    }
    pins.forEach((pin) => {
      specs.push({
        id: pin.id,
        position: pin.position,
        category: getGuideCategory(pin.guide),
        title: pin.guide.title,
        isProperty: false,
      });
    });

    (async () => {
      const created = await Promise.all(
        specs.map(async (spec) => {
          const element = buildPinElement(spec.category, spec.isProperty);
          const marker = await googleMapsService.addMarker(
            map,
            { lat: spec.position.lat, lng: spec.position.lng },
            {
              content: element,
              title: spec.title,
              // AdvancedMarkerElement only dispatches clicks when it is
              // explicitly clickable; the legacy Marker ignores this.
              gmpClickable: true,
            }
          );
          return { spec, marker, element };
        })
      );

      if (cancelled) {
        created.forEach(({ marker }) => removeMarker(marker));
        return;
      }

      // Swap the old set out only now that the new one exists, so the map
      // never flashes empty between updates.
      Object.values(markerStoreRef.current).forEach(({ marker }) =>
        removeMarker(marker)
      );
      markerStoreRef.current = {};

      created.forEach(({ spec, marker, element }) => {
        markerStoreRef.current[spec.id] = { marker, element };
        marker.addListener("click", () => setSelectedId(spec.id));
      });

      applySelection();

      // Frame everything until the guest takes over; after that, leave
      // their view alone.
      if (!interactedRef.current && created.length > 0) {
        const gmaps = window.google.maps;
        if (created.length === 1) {
          map.setCenter(created[0].spec.position);
          map.setZoom(15);
        } else {
          const bounds = new gmaps.LatLngBounds();
          created.forEach(({ spec }) => bounds.extend(spec.position));
          map.fitBounds(bounds, { top: 64, right: 56, bottom: 150, left: 40 });
          gmaps.event.addListenerOnce(map, "idle", () => {
            // Two pins a block apart shouldn't zoom to rooftops.
            if (map.getZoom() > 16) map.setZoom(16);
          });
        }
      }

      updateInView();
    })();

    return () => {
      cancelled = true;
    };
  }, [mapReady, pins, propertyPosition, applySelection, updateInView]);

  // Centre the map on one of the strip's items without touching the zoom.
  const panToItem = useCallback((id) => {
    const map = mapRef.current;
    if (!map) return;
    const position =
      id === PROPERTY_ID
        ? propertyPositionRef.current
        : pinsRef.current.find((pin) => pin.id === id)?.position;
    if (!position) return;
    interactedRef.current = true;
    map.panTo({ lat: position.lat, lng: position.lng });
  }, []);

  // Swiping the strip is navigation: when it comes to rest, the card nearest
  // its centre becomes the selection and the map pans to that place. Only
  // guest-driven scrolls count (see stripUserScrollRef) — the strip's own
  // programmatic moves would otherwise echo back into the map forever.
  const handleStripScroll = () => {
    if (!stripUserScrollRef.current) return;
    clearTimeout(stripScrollTimerRef.current);
    stripScrollTimerRef.current = setTimeout(() => {
      const strip = stripRef.current;
      if (!strip || !stripUserScrollRef.current) return;
      stripUserScrollRef.current = false;

      const centre = strip.scrollLeft + strip.clientWidth / 2;
      let bestId = null;
      let bestDistance = Infinity;
      inViewItems.forEach((item) => {
        const card = cardRefs.current[item.id];
        if (!card) return;
        const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - centre);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestId = item.id;
        }
      });

      if (bestId && bestId !== selectedIdRef.current) {
        lastNavSourceRef.current = "strip";
        setSelectedId(bestId);
        panToItem(bestId);
      }
    }, 150);
  };

  // When the set of in-view places changes, bring the strip somewhere
  // sensible. After a *map* move, that's the start — the new nearest places.
  // After a *strip* move it must stay on the card the guest swiped to, which
  // the pan has just re-sorted to the front, so centre that card instead of
  // yanking back to zero. Left alone entirely while a finger is on the strip.
  const inViewKey = inViewIds.join("|");
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || stripTouchedRef.current) return;
    stripUserScrollRef.current = false;
    if (lastNavSourceRef.current === "strip" && selectedIdRef.current) {
      cardRefs.current[selectedIdRef.current]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    } else {
      strip.scrollTo({ left: 0 });
    }
  }, [inViewKey]);

  useEffect(() => {
    applySelection();
    if (selectedId) {
      // Programmatic scroll — don't let it read back as strip navigation.
      stripUserScrollRef.current = false;
      cardRefs.current[selectedId]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [selectedId, applySelection]);

  // The strip's settle timer survives unmount otherwise.
  useEffect(() => () => clearTimeout(stripScrollTimerRef.current), []);

  const handleLocateUser = () => {
    const map = mapRef.current;
    if (!map) return;

    if (!navigator.geolocation) {
      toast({
        title: "Location isn't available on this device",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setFindingUser(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const position = { lat: coords.latitude, lng: coords.longitude };
        interactedRef.current = true;
        lastNavSourceRef.current = "map";
        map.panTo(position);
        map.setZoom(15);

        removeMarker(userMarkerRef.current);
        try {
          userMarkerRef.current = await googleMapsService.addMarker(
            map,
            position,
            { content: buildUserDot(), title: "You are here" }
          );
        } catch (error) {
          // The pan still worked; the dot is a nicety.
        }
        setFindingUser(false);
      },
      () => {
        setFindingUser(false);
        toast({
          title: "Couldn't get your location",
          description: "Check that location access is allowed for this site.",
          status: "info",
          duration: 4000,
          isClosable: true,
        });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleGoToProperty = () => {
    const map = mapRef.current;
    if (!map || !propertyPosition) return;
    interactedRef.current = true;
    lastNavSourceRef.current = "map";
    map.panTo({ lat: propertyPosition.lat, lng: propertyPosition.lng });
    map.setZoom(15);
  };

  const pinsById = useMemo(() => {
    const lookup = {};
    pins.forEach((pin) => {
      lookup[pin.id] = pin;
    });
    return lookup;
  }, [pins]);

  // One shape for everything in the strip, so the property and the guides
  // render through the same card.
  const inViewItems = inViewIds
    .map((id) => {
      if (id === PROPERTY_ID) {
        return {
          id,
          isProperty: true,
          category: PROPERTY_CATEGORY,
          title: "Your stay",
          subtitle: propertyAddress || "Where you're staying",
        };
      }
      const pin = pinsById[id];
      if (!pin) return null;
      const category = getGuideCategory(pin.guide);
      return {
        id,
        isProperty: false,
        category,
        title: pin.guide.title,
        subtitle: `${category.label} · ${pin.address}`,
        guide: pin.guide,
      };
    })
    .filter(Boolean);

  if (loading) {
    return <Skeleton h="100%" minH="420px" borderRadius="0" />;
  }

  if (located.length === 0) {
    return (
      <Center flexDirection="column" py={12} px={6} textAlign="center" h="100%">
        <Icon as={FaMapMarkerAlt} color="#817EFF" fontSize="32px" mb={3} />
        <Text fontSize="14px" fontWeight={700} color="white" mb={1}>
          No mapped places yet
        </Text>
        <Text fontSize="13px" color="gray.400" maxW="320px">
          Guides that include an address show up here as pins. Browse the other
          tabs to read everything available for your stay.
        </Text>
      </Center>
    );
  }

  if (!canShowMap) {
    // Addresses are still being turned into coordinates.
    if (!resolvedOnce) {
      return (
        <Center h="100%" minH="420px" flexDirection="column" gap={3}>
          <CircularProgress
            isIndeterminate
            size="28px"
            color="#817EFF"
            trackColor="transparent"
          />
          <Text fontSize="13px" color="gray.400">
            Locating places…
          </Text>
        </Center>
      );
    }

    return (
      <Center flexDirection="column" py={12} px={6} textAlign="center" h="100%">
        <Icon as={FaMapMarkerAlt} color="#817EFF" fontSize="32px" mb={3} />
        <Text fontSize="14px" fontWeight={700} color="white" mb={1}>
          Couldn't place these on the map
        </Text>
        <Text fontSize="13px" color="gray.400" maxW="320px">
          The addresses in these guides couldn't be found. You can still read
          each one from the other tabs.
        </Text>
      </Center>
    );
  }

  return (
    <Box position="relative" h="100%" minH="320px" bg="#2D3748">
      <Box ref={containerRef} position="absolute" top={0} right={0} bottom={0} left={0} />

      {!mapReady && !mapError && (
        <Center position="absolute" top={0} right={0} bottom={0} left={0} pointerEvents="none">
          <CircularProgress
            isIndeterminate
            size="28px"
            color="#817EFF"
            trackColor="transparent"
          />
        </Center>
      )}

      {mapError && (
        <Center position="absolute" top={0} right={0} bottom={0} left={0} px={6} textAlign="center">
          <Text fontSize="13px" color="gray.300">
            The map couldn't load. Check your connection and try again.
          </Text>
        </Center>
      )}

      {mapReady && (
        <>
          {/* Status chip */}
          <Flex
            position="absolute"
            top={3}
            left={3}
            align="center"
            gap={2}
            bg="rgba(26, 32, 44, 0.92)"
            borderRadius="full"
            px={3}
            py={2}
            boxShadow="0 2px 8px rgba(0,0,0,0.35)"
          >
            {isLocating && (
              <CircularProgress
                isIndeterminate
                size="12px"
                color="#817EFF"
                trackColor="transparent"
              />
            )}
            <Text fontSize="12px" fontWeight={700} color="white">
              {isLocating
                ? "Locating places"
                : `${inViewIds.length} ${inViewIds.length === 1 ? "place" : "places"} in view`}
            </Text>
          </Flex>

          {/* Jump buttons */}
          <Flex position="absolute" top={3} right={3} direction="column" gap={2}>
            <IconButton
              aria-label="Show places near me"
              icon={<FaLocationArrow />}
              size="sm"
              isRound
              bg="rgba(26, 32, 44, 0.92)"
              color="#A5A3FF"
              boxShadow="0 2px 8px rgba(0,0,0,0.35)"
              _hover={{ bg: "#2D3748" }}
              isLoading={findingUser}
              onClick={handleLocateUser}
            />
            {propertyPosition && (
              <IconButton
                aria-label="Go to your stay"
                icon={<FaHome />}
                size="sm"
                isRound
                bg="rgba(26, 32, 44, 0.92)"
                color="#A5A3FF"
                boxShadow="0 2px 8px rgba(0,0,0,0.35)"
                _hover={{ bg: "#2D3748" }}
                onClick={handleGoToProperty}
              />
            )}
          </Flex>

          {/* Only what's in view — pan or zoom and this follows. */}
          {inViewItems.length > 0 ? (
            <Flex
              ref={stripRef}
              position="absolute"
              left={0}
              right={0}
              bottom={0}
              gap={2}
              px={3}
              pt={2}
              pb={3}
              overflowX="auto"
              // The strip spans the full width, so without this its empty
              // gaps and padding swallow map drags along the bottom edge —
              // the map felt dead down there. Only the cards take touches.
              pointerEvents="none"
              onScroll={handleStripScroll}
              onTouchStart={() => {
                stripTouchedRef.current = true;
                stripUserScrollRef.current = true;
              }}
              onTouchEnd={() => {
                stripTouchedRef.current = false;
              }}
              onTouchCancel={() => {
                stripTouchedRef.current = false;
              }}
              onWheel={() => {
                stripUserScrollRef.current = true;
              }}
              sx={{
                // Mandatory: swiping always comes to rest with one card
                // centred, so "navigate the strip" has a definite answer for
                // which place the guest is on.
                scrollSnapType: "x mandatory",
                overscrollBehaviorX: "contain",
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {inViewItems.map((item) => {
                const { category } = item;
                const isSelected = item.id === selectedId;

                return (
                  <Box
                    key={item.id}
                    as="button"
                    ref={(el) => {
                      cardRefs.current[item.id] = el;
                    }}
                    onClick={() => {
                      setSelectedId(item.id);
                      if (item.isProperty) {
                        // Nothing to open for the property — bring it into
                        // focus on the map instead.
                        handleGoToProperty();
                      } else {
                        onGuideSelect?.(item.guide);
                      }
                    }}
                    flexShrink={0}
                    pointerEvents="auto"
                    w={{ base: "250px", md: "290px" }}
                    textAlign="left"
                    bg="rgba(26, 32, 44, 0.97)"
                    border="1px solid"
                    borderColor={
                      isSelected
                        ? "#817EFF"
                        : item.isProperty
                        ? "rgba(129, 126, 255, 0.5)"
                        : "whiteAlpha.300"
                    }
                    borderRadius="14px"
                    px={3}
                    py={3}
                    boxShadow="0 4px 14px rgba(0,0,0,0.4)"
                    transition="all 0.15s ease"
                    _hover={{ borderColor: "rgba(129, 126, 255, 0.6)" }}
                    _active={{ transform: "scale(0.99)" }}
                    sx={{ scrollSnapAlign: "center" }}
                  >
                    <Flex align="center" gap={3}>
                      <Center
                        flexShrink={0}
                        w="36px"
                        h="36px"
                        borderRadius="full"
                        bg={category.color}
                      >
                        <Icon
                          as={category.icon}
                          color={item.isProperty ? "#504CFF" : "white"}
                          fontSize="15px"
                        />
                      </Center>
                      <Box flex="1" minW={0}>
                        <Text
                          fontSize="14px"
                          fontWeight={700}
                          color="white"
                          noOfLines={1}
                        >
                          {item.title}
                        </Text>
                        <Text fontSize="12px" color="gray.400" noOfLines={1}>
                          {item.subtitle}
                        </Text>
                      </Box>
                      {!item.isProperty && (
                        <Icon
                          as={FaChevronRight}
                          color="#817EFF"
                          fontSize="11px"
                          flexShrink={0}
                        />
                      )}
                    </Flex>
                  </Box>
                );
              })}
            </Flex>
          ) : (
            <Flex position="absolute" left={0} right={0} bottom={0} justify="center" px={3} pb={4}>
              <Box
                bg="rgba(26, 32, 44, 0.95)"
                borderRadius="full"
                px={4}
                py={2}
                boxShadow="0 2px 8px rgba(0,0,0,0.35)"
              >
                <Text fontSize="12px" fontWeight={600} color="gray.300">
                  No places here — drag or zoom out to find some
                </Text>
              </Box>
            </Flex>
          )}
        </>
      )}
    </Box>
  );
};

export default GuidesMap;
